import React, { useMemo } from 'react';

import { BuilderComponent } from '@builder.io/react';
import config from 'config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';

import PromoTrackingWrapper from '~/builder/components/PromoTrackingWrapper/PromoTrackingWrapper';
import { BUILDER_MODELS } from '~/builder/constants/models';
import { getBuilderJsonSSR } from '~/builder/utils/getBuilderJsonSSR';
import { getDeprecatedFieldClasses } from '~/builder/utils/getDeprecatedFieldClasses';
import { isBuilderContentReady } from '~/builder/utils/isBuilderContentReady';
import { isBuilderPreview } from '~/builder/utils/isBuilderPreview';
import {
  allComponents,
  registerBuilderComponents,
  registerBuilderMenu,
} from '~/builder/utils/registerBuilderComponents';
import { useBuilderContent } from '~/builder/utils/useBuilderContent';
import useBuilderJson from '~/builder/utils/useBuilderJson';
import { useTrackBuilderTestOnContentLoaded } from '~/builder/utils/useTrackBuilderTestOnContentLoaded';
import DefaultLayout from '~/layout/components/DefaultLayout';
import { AssortmentTestProvider } from '~/pdp/context/AssortmentTestContext';
import { getConfigValueSSR } from '~/shared/utils/config';
import { useBrandDomainCanonicalLink } from '~/shared/utils/useBrandDomainCanonicalLink';
import { StyleContext } from '~/techstyle-shared/react-components';

import createPage from '../../registry';
import NotFoundPage from '../404';

function BuilderPage({ builderJson, builderStateData = {}, builderJsonKey }) {
  const canonicalPath = useBrandDomainCanonicalLink();
  const router = useRouter();
  const { asPath } = router;
  const pathname = asPath?.split('?')[0];
  const builderProperties = {
    modelName: BUILDER_MODELS.PAGE,
    urlPath: pathname,
    ssrBuilderJson: builderJson,
    ssrBuilderJsonKey: builderJsonKey,
  };
  const { builderJson: clientBuilderJson, isLoading: builderContentLoading } =
    useBuilderJson(builderProperties);
  const builderJsonToRender = clientBuilderJson || builderJson;
  const { content, locale } = useBuilderContent(builderJsonToRender);

  const pageNotFound =
    !builderContentLoading && !content && !isBuilderPreview();
  const builderContentLoaded = isBuilderContentReady({
    isLoading: builderContentLoading,
    content,
  });

  const deprecatedBuilderFields = useMemo(() => {
    return getDeprecatedFieldClasses(builderJsonToRender);
  }, [builderJsonToRender]);

  let title = config.get('public.brand.label');
  let description = title;
  let seoNoIndex;
  let noHeader = false;
  let noFooter = false;

  if (content?.data) {
    const {
      title: contentTitle,
      description: contentDescription,
      seoNoIndex: contentSeoNoIndex,
      hideHeader,
      hideFooter,
    } = content.data;
    title = contentTitle;
    description = contentDescription;
    seoNoIndex = contentSeoNoIndex;
    noHeader = hideHeader;
    noFooter = hideFooter;
  }

  const trackBuilderTestOnContentLoaded = useTrackBuilderTestOnContentLoaded();

  return (
    <StyleContext>
      <DefaultLayout
        title={title}
        description={description}
        noHeader={noHeader}
        noFooter={noFooter}
        canonicalPath={canonicalPath}
        noIndex={seoNoIndex}
        data-builder-deprecated-field={deprecatedBuilderFields?.join()}
      >
        {pageNotFound && <NotFoundPage />}
        {builderContentLoaded && (
          <PromoTrackingWrapper>
            <AssortmentTestProvider
              activeAssortmentTests={
                builderStateData?.activeAssortmentTests || []
              }
            >
              <BuilderComponent
                model="page"
                content={content}
                locale={locale}
                data={builderStateData}
                options={{
                  enrich: true,
                }}
                contentLoaded={trackBuilderTestOnContentLoaded}
              />
            </AssortmentTestProvider>
          </PromoTrackingWrapper>
        )}
      </DefaultLayout>
    </StyleContext>
  );
}

BuilderPage.getInitialProps = async (ctx) => {
  const pathname = ctx.asPath?.split('?') ? ctx.asPath?.split('?')[0] : null;

  const [
    { builderJson, builderStateData, key: builderJsonKey },
    activeAssortmentTests,
  ] = await Promise.all([
    getBuilderJsonSSR({
      modelName: BUILDER_MODELS.PAGE,
      urlPath: pathname,
      ctx,
    }),
    getConfigValueSSR(ctx, 'public.brand.assortmentTests'),
  ]);

  return {
    builderJson,
    builderJsonKey,
    builderStateData: {
      ...builderStateData,
      activeAssortmentTests: activeAssortmentTests || [],
    },
  };
};

BuilderPage.propTypes = {
  builderJson: PropTypes.object,
  builderJsonKey: PropTypes.string,
  builderStateData: PropTypes.object,
};

registerBuilderComponents(allComponents);
registerBuilderMenu();

export default createPage(BuilderPage, {
  resourceBundles: [
    'app_grid',
    'grid',
    'grid_static',
    'grid_dynamic',
    'contact_form',
    'site_home',
    'site_product_category',
    'site_product_filter',
    'site_product_grid',
    'site_home_boutique',
    'top_sellers_listing',
  ],
});
